import React from "react";
import { EnumUserGroup, ErrorList, ErrorStrings } from "../../../components/Enums";
import {
    ErrorText,
    FlexContainer,
    PortalFormDescription,
    PortalFormFieldGroup,
    PortalFormLabel,
    PortalFormLinkButton,
    PortalSelectedIcon,
    PortalSelectedItem,
    PortalSelectedItemContent
} from "src/components";
import Skeleton from "src/scenes/Statistics/components/Skeleton";
import UserGroup from "src/img/survey/updatedIcons/users.svg";
import VisibilityModal from "../VisibilityModal";
import VisibilitySection from "../VisibilitySection";
import { TableType } from "src/constants/survey";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";

const renderSelectedPeopleItem = (label, count, onClick, isAll) => (
    <PortalSelectedItem>
        <PortalSelectedItemContent>
            <PortalSelectedIcon>
                <img src={UserGroup} alt="peopleIcon" height="14px" style={{ margin: "auto" }} />
            </PortalSelectedIcon>
            <span>
                {isAll ? `All ${label === "Employees" ? capitalizeString(pluralize(customNames.employees)) : capitalizeString(pluralize(customNames.clients))}` : `${count} ${count === 1 ? label.slice(0, -1) : label}`}
            </span>
        </PortalSelectedItemContent>
        <PortalFormLinkButton type="button" onClick={onClick}>
            Edit
        </PortalFormLinkButton>
    </PortalSelectedItem>
);

const renderSelectedPeopleSection = (
    label, peopleInPoll, countInPoll, isEditSurveyForm,
    surveyCount, totalSurveyCount, userGroup, toggleModal
) => {
    const isNotTargetedSurvey = (
        userGroup === EnumUserGroup.ALL_USERS ||
        userGroup === EnumUserGroup.EMPLOYEES ||
        userGroup === EnumUserGroup.CLIENTS
    );
    if (peopleInPoll || countInPoll > 0) {
        return renderSelectedPeopleItem(label, countInPoll, toggleModal, peopleInPoll);
    } else if (isEditSurveyForm && surveyCount > 0 && !isNotTargetedSurvey) {
        const isTotalMatch = totalSurveyCount === surveyCount;
        return renderSelectedPeopleItem(label, surveyCount, toggleModal, isTotalMatch);
    } else if (isEditSurveyForm && isNotTargetedSurvey && userGroup === label) {
        return renderSelectedPeopleItem(label, 0, toggleModal, true);
    } else if (isEditSurveyForm && isNotTargetedSurvey && userGroup === EnumUserGroup.ALL_USERS) {
        return renderSelectedPeopleItem(label, 0, toggleModal, true);
    } else {
        return null;
    }
};

export const UserGroupField = (props) => {
    const {
        inputErrors,
        isEditSurveyForm,
        survey,
        loading,
        togglePollVisibilityModal,
        showPollVisibilityModal,
        handleSaveSelectedUsers,
        selectedUsers,
        clientsInPoll,
        employeesInPoll,
        employeesCountInPoll,
        clientsCountInPoll,
        isEditForm,
        isShowInitialUserGroupAddButton
    } = props;

    const getErrorStrings = (...errs) => inputErrors.filter(err => errs.includes(err)).map(err => ErrorStrings[err]);
    const userGroupError = getErrorStrings(ErrorList.EMPTY_USER_GROUP);
    const selectedUsersError = getErrorStrings(ErrorList.EMPTY_SELECTED_USERS);

    return (
        <PortalFormFieldGroup>
            <PortalFormLabel>
                Select User Group or Specific Users
            </PortalFormLabel>
            <PortalFormDescription>
                Select to send this survey to All {capitalizeString(pluralize(customNames.employees))}, All {capitalizeString(pluralize(customNames.clients))}, or selected users.
            </PortalFormDescription>

            { loading ?
                <Skeleton height="50px" width="100%" animation="wave" />
                :
                <>
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <VisibilitySection
                            togglePollVisibilityModal={togglePollVisibilityModal}
                            isEditForm={isEditForm}
                            pollVisibilityError={selectedUsersError}
                            label="Select Users"
                            buttonWidth="17.5rem"
                            showButton={isShowInitialUserGroupAddButton}
                        />
                        <FlexContainer direction="row" gap="1rem" wrap="wrap">
                            {renderSelectedPeopleSection(
                                "Employees",
                                employeesInPoll,
                                employeesCountInPoll,
                                isEditSurveyForm,
                                survey.employeeCount,
                                survey.totalEmployeeCount,
                                survey.userGroup,
                                togglePollVisibilityModal
                            )}
                            {renderSelectedPeopleSection(
                                "Clients",
                                clientsInPoll,
                                clientsCountInPoll,
                                isEditSurveyForm,
                                survey.clientCount,
                                survey.totalClientCount,
                                survey.userGroup,
                                togglePollVisibilityModal
                            )}
                        </FlexContainer>
                    </div>

                    <VisibilityModal
                        togglePollVisibilityModal={togglePollVisibilityModal}
                        showPollVisibilityModal={showPollVisibilityModal}
                        handleSave={handleSaveSelectedUsers}
                        pollParticipantUuids={isEditForm ? survey.usersSelectedUuid : selectedUsers}
                        clientsInPoll={isEditForm ? survey.totalClientCount === survey.clientCount : clientsInPoll}
                        employeesInPoll={isEditForm ? survey.totalEmployeeCount === survey.employeeCount : employeesInPoll}
                        employeesCountInPoll={isEditForm ? survey.employeeCount : employeesCountInPoll}
                        clientsCountInPoll={isEditForm ? survey.clientCount : clientsCountInPoll}
                        modalTitle="Choose Specific Survey Participants"
                        formType={TableType.Surveys}
                        userGroupType={survey.userGroup}
                    />
                </>
            }

            { userGroupError.length > 0 &&
                <ErrorText errorMargin="15px 20px 15px">
                    {userGroupError.length > 0 ? userGroupError : null}
                </ErrorText>
            }
        </PortalFormFieldGroup>
    );
};
